import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowsClockwise,
  Brain,
  Check,
  Cpu,
  Eye,
  FileText,
  ListChecks,
  Shield
} from '@phosphor-icons/react';

// Orion character image
const ORION_IMAGE = "https://customer-assets.emergentagent.com/job_orion-ai-4/artifacts/pxvnoi7t_Orion%201.png";

const OrionLanding = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white overflow-x-hidden">
      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 1: HERO (Above the Fold)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background gradient layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1a] via-[#0d1424] to-[#0a1628]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1a] via-transparent to-transparent" />
        
        {/* Orion image - positioned to emerge from the right */}
        <div className="absolute right-0 top-0 bottom-0 w-[55%] lg:w-[50%] pointer-events-none">
          {/* Soft glow behind Orion */}
          <div className="absolute inset-0 bg-gradient-to-l from-cyan-500/8 via-cyan-500/5 to-transparent" />
          <div className="absolute top-1/2 right-[15%] -translate-y-1/2 w-[400px] h-[400px] bg-cyan-400/10 rounded-full blur-[100px]" />
          
          {/* Orion image with gradient fade */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-full h-full">
              {/* Gradient overlay for seamless blend */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1a] via-[#0a0f1a]/60 to-transparent z-10" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a]/80 via-transparent to-[#0a0f1a]/40 z-10" />
              
              <img
                src={ORION_IMAGE}
                alt="Orion"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-[90%] max-h-[700px] w-auto object-contain object-right"
              />
            </div>
          </div>
        </div>
        
        {/* Hero content */}
        <div className="relative z-20 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="max-w-2xl">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
                  Meet Orion.
                  <br />
                  <span className="text-gray-400">Your AI operator that gets things done.</span>
                </h1>
                
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Orion doesn't just respond — it executes.
                  <br />
                  It plans, runs tasks step by step, and shows you exactly what's happening.
                </p>
                
                <p className="text-base text-gray-500">
                  Built for people who want outcomes, not explanations.
                </p>
                
                <p className="text-base text-gray-400 pt-2">
                  <span className="text-cyan-400/80 font-medium">Autonomous, but always with you in control.</span>
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/register"
                  data-testid="orion-hero-cta-primary"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-[#0a0f1a] font-medium text-lg hover:bg-gray-100 transition-all hover:scale-[1.02]"
                >
                  Try Orion
                </Link>
                <a
                  href="#what-orion-does"
                  data-testid="orion-hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 text-gray-300 hover:text-white transition-colors group"
                >
                  See what Orion can do
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 2: CREDIBILITY STRIP
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-12 border-y border-gray-800/40 bg-[#080c14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16 text-sm text-gray-400">
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-cyan-400/60" />
              <span>Designed with autonomy and safety at its core</span>
            </div>
            <div className="flex items-center gap-3">
              <Eye size={16} className="text-cyan-400/60" />
              <span>Read-only by default. Approval when it matters.</span>
            </div>
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={16} className="text-cyan-400/60" />
              <span>Calm, reliable, and persistent by design</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 3: REFRAME
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-medium mb-8">
            More than chat. Work you can see.
          </h2>
          
          <p className="text-lg lg:text-xl text-gray-400 leading-relaxed mb-10">
            Orion turns requests into clear tasks, runs them step by step, and shows you exactly what's happening as it works.
            <br />
            <span className="text-white">No guessing. No silent stops. You always know where things are at.</span>
          </p>
          
          <p className="text-xl lg:text-2xl font-medium text-white">
            Because finishing the work matters more than talking about it.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 3.5: HOW ORION WORKS (3-step strip)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[#080c14] border-y border-gray-800/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h3 className="text-xl font-medium text-center mb-10 text-gray-300">How Orion Works</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-cyan-400 font-semibold">1</span>
              </div>
              <h4 className="font-medium mb-2">Plans the work</h4>
              <p className="text-sm text-gray-500">Breaks your request into clear steps</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-cyan-400 font-semibold">2</span>
              </div>
              <h4 className="font-medium mb-2">Executes across tools</h4>
              <p className="text-sm text-gray-500">Runs commands, handles files, uses connected systems</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-cyan-400 font-semibold">3</span>
              </div>
              <h4 className="font-medium mb-2">Shows the outcome</h4>
              <p className="text-sm text-gray-500">Tasks, progress, and results are visible as they happen</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 4: CORE DIFFERENTIATORS
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section id="what-orion-does" className="py-24 lg:py-32 bg-[#080c14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                icon: ListChecks,
                title: "Agentic Execution",
                desc: "Orion breaks requests into clear tasks, executes them step by step, and shows you the progress."
              },
              {
                icon: Brain,
                title: "Capability-Aware",
                desc: "Orion understands what it can do — and never refuses achievable tasks."
              },
              {
                icon: Eye,
                title: "Read-Only First",
                desc: "Public information runs autonomously. Sensitive actions always require your approval."
              },
              {
                icon: FileText,
                title: "Verified Outputs",
                desc: "Files generated by Orion are saved safely to a verified workspace and always available to download."
              }
            ].map((card, i) => (
              <div 
                key={i}
                className="p-6 lg:p-8 rounded-2xl bg-gradient-to-b from-gray-800/20 to-gray-900/20 border border-gray-800/40 hover:border-gray-700/50 transition-all duration-300"
              >
                <card.icon size={24} className="text-cyan-400/80 mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-medium mb-3">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 5: WHAT ORION CAN HELP WITH
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-medium mb-4">
              What Orion can help with
            </h2>
            <p className="text-gray-500">
              Orion works across real-world tasks, not just conversations.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {[
              "Check live information like weather, markets, and news",
              "Monitor systems and surface issues before they become problems",
              "Review unread emails and draft replies for approval",
              "Track websites or data that change over time",
              "Suggest automations when it detects repetition"
            ].map((item, i) => (
              <div 
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl bg-gray-800/15 border border-gray-800/30"
              >
                <Check size={18} className="text-cyan-400/70 mt-0.5 flex-shrink-0" strokeWidth={2} />
                <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 text-sm italic">
            And it keeps learning how to be more useful — without nagging.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 6: TRUST & SAFETY
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#080c14]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-medium mb-4">
              Autonomous doesn't mean reckless.
            </h2>
            <p className="text-gray-500">
              Orion is built with strict boundaries:
            </p>
          </div>
          
          <div className="space-y-4 max-w-lg mx-auto mb-10">
            {[
              "Orion never asks for passwords",
              "Credentials and sensitive access are blocked by design",
              "Read-only tasks run automatically",
              "Anything that writes, sends, or changes requires your approval",
              "You're always in control"
            ].map((point, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-7 h-7 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-cyan-400" strokeWidth={2.5} />
                </div>
                <span className="text-gray-300 text-sm">{point}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 text-sm">
            Quiet autonomy. Clear limits.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 6.5: TALK TO ORION ANYWHERE
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-medium mb-4">
              Talk to Orion wherever you are
            </h2>
            <p className="text-gray-400">
              Reach Orion through the channels you already use.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              {
                title: "Platform Chat",
                desc: "Full-featured interface in your workspace",
                icon: "💬"
              },
              {
                title: "Telegram",
                desc: "Quick tasks and updates on the go",
                icon: "✈️"
              },
              {
                title: "WhatsApp",
                desc: "Familiar messaging, powerful results",
                icon: "📱"
              }
            ].map((channel, i) => (
              <div 
                key={i}
                className="p-6 rounded-2xl bg-gradient-to-b from-gray-800/20 to-gray-900/20 border border-gray-800/40 hover:border-cyan-500/30 transition-all duration-300 text-center"
              >
                <div className="text-3xl mb-4">{channel.icon}</div>
                <h3 className="text-lg font-medium mb-2">{channel.title}</h3>
                <p className="text-gray-500 text-sm">{channel.desc}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-8">
            Same Orion. Same capabilities. Your choice of channel.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 7: FINAL CTA
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-medium mb-4">
            Let Orion handle the checking.
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            You focus on decisions. Orion handles the work.
          </p>
          
          <Link
            to="/register"
            data-testid="orion-final-cta"
            className="inline-flex items-center justify-center px-10 py-4 rounded-full bg-white text-[#0a0f1a] font-medium text-lg hover:bg-gray-100 transition-all hover:scale-[1.02]"
          >
            Get started with Orion
          </Link>
          
          <p className="text-sm text-gray-600 mt-6">
            Takes minutes to set up. No commitment.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          SECTION 8: FOOTER (MINIMAL)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <footer className="py-10 border-t border-gray-800/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col items-center gap-6">
            <p className="text-xs text-gray-600">
              Built on modern agent protocols (MCP) for reliable multi-step execution.
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy</Link>
              <span className="text-gray-700">·</span>
              <Link to="/security" className="hover:text-gray-300 transition-colors">Security</Link>
              <span className="text-gray-700">·</span>
              <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms</Link>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600 mb-1">© Man & Machine</p>
              <p className="text-xs text-gray-600">Orion is part of Man & Machine</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OrionLanding;
